import type { Catalog, Lang, Localized, Question } from "./types.ts";

/**
 * Chinese question text is maintained separately from the scraped catalog so
 * re-scraping upstream never discards it.
 */
type ZhStore = Record<
  string,
  {
    text?: string;
    pretext?: string;
    answers?: string[];
    unit?: string;
    explanation?: string;
  }
>;

export interface LoadedCatalog extends Catalog {
  byId: Map<number, Question>;
  byNumber: Map<string, Question>;
  categoryTitle: (id: number, lang: Lang) => string;
}

/**
 * Content-hashed catalogue filenames, resolved at build time so there is no
 * manifest to fetch before the data itself. See vite.config.ts.
 */
declare const __DATA_FILES__: { catalog: string; zh: string } | undefined;

export const DATA_FILES =
  typeof __DATA_FILES__ === "object" && __DATA_FILES__
    ? __DATA_FILES__
    : { catalog: "catalog.json", zh: "zh.json" };

export async function loadCatalog(): Promise<LoadedCatalog> {
  const [catalog, zh] = await Promise.all([
    fetch(`/data/${DATA_FILES.catalog}`).then((r) => {
      if (!r.ok) throw new Error(`${DATA_FILES.catalog}: ${r.status}`);
      return r.json() as Promise<Catalog>;
    }),
    // Absent or partial translation file is fine — we fall back per field.
    fetch(`/data/${DATA_FILES.zh}`)
      .then((r) => (r.ok ? (r.json() as Promise<ZhStore>) : ({} as ZhStore)))
      .catch(() => ({} as ZhStore)),
  ]);

  for (const q of catalog.questions) {
    const tr = zh[String(q.id)];
    if (!tr) continue;
    if (tr.text) q.text.zh = tr.text;
    if (tr.pretext) q.pretext.zh = tr.pretext;
    if (tr.explanation) q.explanation.zh = tr.explanation;
    if (tr.unit && q.numeric) q.numeric.unit.zh = tr.unit;
    if (tr.answers) {
      tr.answers.forEach((a, i) => {
        if (a && q.answers[i]) q.answers[i]!.text.zh = a;
      });
    }
  }
  if (!catalog.languages.includes("zh")) catalog.languages.push("zh");

  const byId = new Map(catalog.questions.map((q) => [q.id, q]));
  const byNumber = new Map(catalog.questions.map((q) => [q.number, q]));
  const cats = new Map(catalog.categories.map((c) => [c.id, c]));

  return {
    ...catalog,
    byId,
    byNumber,
    categoryTitle: (id, lang) => pick(cats.get(id)?.titles ?? {}, lang) || `#${id}`,
  };
}

/**
 * Resolves localized text, falling back en -> de so a missing translation
 * still leaves the question answerable.
 */
export function pick(value: Localized, lang: Lang): string {
  return value[lang] ?? value.en ?? value.de ?? "";
}

/** True when `lang` has its own text rather than a fallback. */
export function hasTranslation(value: Localized, lang: Lang): boolean {
  return typeof value[lang] === "string" && value[lang] !== "";
}

/** Language-specific formula graphics are stored per upstream language code. */
export function resolveMediaLang(html: string, lang: Lang): string {
  return html.replace(/\$LANG/g, lang === "de" ? "de" : "gb");
}

export interface Topic {
  /** Category id of the topic node itself. */
  id: number;
  title: string;
  questionIds: number[];
}

/**
 * The catalogue nests categories three deep: two roots (Grundstoff /
 * Zusatzstoff), ~14 subjects under them, then fine-grained leaves. Subjects are
 * the useful level to practise and report on, so climb to that.
 *
 * Both roots carry a subject of the same name (e.g. "Gefahrenlehre"), so
 * subjects are merged by title into one topic.
 */
export function makeTopicResolver(catalog: Pick<Catalog, "categories">) {
  const parentOf = new Map(catalog.categories.map((c) => [c.id, c.parent]));
  return (categoryId: number) => climb(parentOf, categoryId);
}

function climb(parentOf: Map<number, number | null>, id: number): number | null {
  if (!parentOf.has(id)) return null;
  let cur = id;
  const seen = new Set<number>();
  while (!seen.has(cur)) {
    seen.add(cur);
    const parent = parentOf.get(cur);
    if (parent == null) return cur;
    if (parentOf.get(parent) == null) return cur;
    cur = parent;
  }
  return cur;
}

/** Builds the practise-by-subject list, sorted by question count. */
export function buildTopics(catalog: LoadedCatalog, lang: Lang): Topic[] {
  const topicOf = makeTopicResolver(catalog);
  const byTitle = new Map<string, Topic>();

  for (const q of catalog.questions) {
    const node = q.categoryIds.map(topicOf).find((n) => n != null);
    if (node == null) continue;
    const title = catalog.categoryTitle(node, lang);
    let topic = byTitle.get(title);
    if (!topic) {
      topic = { id: node, title, questionIds: [] };
      byTitle.set(title, topic);
    }
    topic.questionIds.push(q.id);
  }

  return [...byTitle.values()].sort(
    (a, b) => b.questionIds.length - a.questionIds.length
  );
}
